import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../core/services/api.service';
import { KobbleClient, NotificationSettings, PointsConfig } from '../../core/models/api.models';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly fb = inject(FormBuilder);

  readonly loading = signal(false);
  readonly savingPoints = signal(false);
  readonly savingReminders = signal(false);
  readonly syncing = signal(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<string | null>(null);
  readonly config = signal<PointsConfig | null>(null);
  readonly notificationSettings = signal<NotificationSettings | null>(null);
  readonly clients = signal<KobbleClient[]>([]);
  readonly selectedClientId = signal<string | null>(null);

  readonly pointsForm = this.fb.nonNullable.group({
    points_earn_per_aud: [1, [Validators.required, Validators.min(1)]],
    points_per_aud: [50, [Validators.required, Validators.min(1)]],
    merchant_kobble_wallet_id: [''],
    subscription_payment_provider: ['stripe' as 'stripe' | 'rentwize', [Validators.required]],
  });

  readonly reminderForm = this.fb.nonNullable.group({
    enabled: [true],
    lead_value: [1, [Validators.required, Validators.min(1)]],
    lead_unit: ['days' as 'hours' | 'days', [Validators.required]],
  });

  reminderHoursPreview(): number {
    const v = this.reminderForm.getRawValue();
    const n = Number(v.lead_value) || 0;
    const hours = v.lead_unit === 'days' ? n * 24 : n;
    return Math.min(720, Math.max(1, hours || 1));
  }

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    let pending = 3;
    const errors: string[] = [];
    const done = () => {
      pending -= 1;
      if (pending <= 0) {
        this.loading.set(false);
        this.error.set(errors.length ? errors.join(' · ') : null);
      }
    };

    this.api.getPointsConfig().subscribe({
      next: (cfg) => {
        this.applyConfig(cfg);
        done();
      },
      error: (err: { message?: string }) => {
        errors.push(err?.message || 'Failed to load points config');
        done();
      },
    });

    this.api.getNotificationSettings().subscribe({
      next: (settings) => {
        this.applyReminderSettings(settings);
        done();
      },
      error: (err: { message?: string }) => {
        errors.push(err?.message || 'Failed to load notification settings');
        done();
      },
    });

    this.api.getKobbleClients().subscribe({
      next: (res) => {
        // Staging may still return a bare array; new API returns { clients, stored }
        const list = Array.isArray(res)
          ? res
          : Array.isArray((res as { clients?: KobbleClient[] })?.clients)
            ? (res as { clients: KobbleClient[] }).clients
            : [];
        this.clients.set(list);
        const stored = !Array.isArray(res) ? (res as { stored?: Partial<PointsConfig> })?.stored : undefined;
        if (stored) {
          this.applyConfig({ ...(this.config() || {}), ...stored } as PointsConfig);
        }
        const storedId = stored?.merchant_kobble_client_id;
        if (storedId) this.selectedClientId.set(String(storedId));
        else if (list[0]?.id) this.selectedClientId.set(String(list[0].id));
        if (!list.length) {
          errors.push('Kobble clients list was empty — check staging backend is deployed and Kobble credentials work');
        }
        done();
      },
      error: (err: { message?: string }) => {
        this.clients.set([]);
        errors.push(err?.message || 'Failed to load Kobble clients from Rentwize API (not direct Kobble)');
        done();
      },
    });
  }

  private applyConfig(cfg: PointsConfig): void {
    this.config.set(cfg);
    this.pointsForm.patchValue({
      points_earn_per_aud: cfg.points_earn_per_aud ?? 1,
      points_per_aud: cfg.points_per_aud ?? 50,
      merchant_kobble_wallet_id: cfg.merchant_kobble_wallet_id || '',
      subscription_payment_provider:
        cfg.subscription_payment_provider === 'rentwize' ? 'rentwize' : 'stripe',
    });
  }

  private applyReminderSettings(settings: NotificationSettings): void {
    this.notificationSettings.set(settings);
    const hours = Math.max(1, Number(settings.rent_due_reminder_hours) || 24);
    const useDays = hours % 24 === 0 && hours >= 24;
    this.reminderForm.patchValue({
      enabled: Number(settings.rent_due_reminder_enabled) === 1,
      lead_value: useDays ? hours / 24 : hours,
      lead_unit: useDays ? 'days' : 'hours',
    });
  }

  savePoints(): void {
    if (this.pointsForm.invalid || this.savingPoints()) {
      this.pointsForm.markAllAsTouched();
      this.error.set('Enter valid points rates');
      return;
    }
    this.savingPoints.set(true);
    this.error.set(null);
    this.success.set(null);
    const v = this.pointsForm.getRawValue();
    this.api
      .updatePointsConfig({
        points_earn_per_aud: Number(v.points_earn_per_aud),
        points_per_aud: Number(v.points_per_aud),
        merchant_kobble_wallet_id: v.merchant_kobble_wallet_id?.trim() || null,
        subscription_payment_provider: v.subscription_payment_provider,
      })
      .subscribe({
        next: (cfg) => {
          this.applyConfig(cfg);
          this.savingPoints.set(false);
          this.success.set('Configuration saved');
        },
        error: (err: { message?: string }) => {
          this.savingPoints.set(false);
          this.error.set(err?.message || 'Failed to save configuration');
        },
      });
  }

  saveReminders(): void {
    if (this.reminderForm.invalid || this.savingReminders()) {
      this.reminderForm.markAllAsTouched();
      this.error.set('Enter a valid lead time (1–720 hours)');
      return;
    }
    const hours = this.reminderHoursPreview();
    if (hours < 1 || hours > 720) {
      this.error.set('Lead time must be between 1 and 720 hours');
      return;
    }
    this.savingReminders.set(true);
    this.error.set(null);
    this.success.set(null);
    this.api
      .updateNotificationSettings({
        rent_due_reminder_enabled: this.reminderForm.getRawValue().enabled ? 1 : 0,
        rent_due_reminder_hours: hours,
      })
      .subscribe({
        next: (settings) => {
          this.applyReminderSettings(settings);
          this.savingReminders.set(false);
          this.success.set('Rent due reminder settings saved');
        },
        error: (err: { message?: string }) => {
          this.savingReminders.set(false);
          this.error.set(err?.message || 'Failed to save reminder settings');
        },
      });
  }

  selectClient(id: string): void {
    this.selectedClientId.set(id);
  }

  syncClient(clientId?: string | null): void {
    const id = clientId || this.selectedClientId();
    if (!id || this.syncing()) return;
    this.syncing.set(true);
    this.error.set(null);
    this.success.set(null);
    const walletOverride = this.pointsForm.getRawValue().merchant_kobble_wallet_id?.trim();
    this.api
      .syncMerchantWallet({
        client_id: id,
        ...(walletOverride ? { merchant_kobble_wallet_id: walletOverride } : {}),
      })
      .subscribe({
        next: (cfg) => {
          this.applyConfig(cfg);
          this.syncing.set(false);
          this.success.set(
            cfg.merchant_configured
              ? 'Merchant client + wallet stored'
              : 'Merchant client stored (wallet still missing — set merchant wallet ID and save, or re-sync when Kobble provisions client_wallet_id)'
          );
          this.reload();
        },
        error: (err: { message?: string }) => {
          this.syncing.set(false);
          this.error.set(err?.message || 'Failed to sync merchant client');
        },
      });
  }
}
